import React from "react";
import { Stack, Typography } from "@financeable-com-au/financeable-ui";
import { toHemSplitOff, type ServiceabilityProfile, type ServiceabilityResult } from "@/utils/serviceability";
import { MARITAL_STATUSES } from "@/constants";
import { fCurrencyDecimal } from "@/utils/formatNumber";

/** Theme tokens rather than literal colours, so the rows follow the design system in both themes. */
const MUTED_TEXT_COLOR = "neutral.400";
const TEXT_COLOR = "text.primary";
const SURPLUS_TEXT_COLOR = "success.plainColor";
const DEFICIT_TEXT_COLOR = "danger.plainColor";
const STRIKE_THROUGH = { textDecoration: "line-through" };

interface SummaryRowProps {
  label: string;
  value: number | null;
  /** Greys out and strikes through a figure the totals ignore, so only the live one reads as live. */
  isUnused?: boolean;
  isBold?: boolean;
  valueColor?: string;
}

interface ServiceabilityBreakdownProps {
  serviceability: ServiceabilityResult;
  customerProfile?: ServiceabilityProfile | null;
  /** Captions the figures when the totals come from each item's own share rather than one shared percentage. */
  isUsingIndividualShares?: boolean;
}

const SummaryRow = ({ label, value, isUnused = false, isBold = false, valueColor }: SummaryRowProps) => {
  const rowColor = isUnused ? MUTED_TEXT_COLOR : TEXT_COLOR;
  const rowStyle = isUnused ? STRIKE_THROUGH : undefined;

  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography level="body-sm" bold={isBold} textColor={rowColor} style={rowStyle}>
        {label}
      </Typography>
      <Typography level="body-sm" bold={isBold} textColor={valueColor ?? rowColor} style={rowStyle}>
        {fCurrencyDecimal(value ?? 0)}
      </Typography>
    </Stack>
  );
};

/** Label for the HEM benchmark row, e.g. "HEM (couple) w/ 2 children". */
const getHemLabel = (customerProfile?: ServiceabilityProfile | null): string => {
  if (!customerProfile?.maritalStatus) return "HEM";

  const household = customerProfile.maritalStatus === MARITAL_STATUSES.MARRIED_DEFACTO ? "couple" : "single";
  const dependants = Number(customerProfile?.numberOfDependants) || 0;
  if (dependants === 0) return `HEM (${household})`;

  return `HEM (${household}) w/ ${dependants} ${dependants > 1 ? "children" : "child"}`;
};

/** Read-only monthly serviceability summary, including how the HEM benchmark was split. */
const ServiceabilityBreakdown = React.memo(
  ({ serviceability, customerProfile, isUsingIndividualShares = false }: ServiceabilityBreakdownProps) => {
    const {
      income,
      liabilities,
      rent,
      statedHouseholdExpenses,
      hemBenchmark,
      hemApplied,
      hemShare,
      isHemGreater,
      expenses,
      surplus,
    } = serviceability;

    // hemShare is the portion the applicant carries; the row is labelled with the portion split off, matching the
    // slider. The conversion is shared with the slider and its caption so the three cannot disagree.
    const hemSplitPercent = Math.round(toHemSplitOff(hemShare) * 100);
    const isHemSplit = hemBenchmark !== null && hemSplitPercent > 0;

    // Exactly one of these three figures feeds the totals; the other two are struck through so it is clear which.
    // Once the benchmark is split it is the split figure that applies, so the full benchmark reads as superseded.
    const isFullHemUnused = isHemSplit || !isHemGreater;
    const isHemAppliedUnused = !isHemGreater;
    const isStatedExpensesUnused = isHemGreater;

    return (
      <Stack direction="column" width={"50%"} rowGap={2}>
        <Typography level="title-sm">Serviceability (monthly)</Typography>
        {isUsingIndividualShares && (
          <Typography level="body-xs" textColor={MUTED_TEXT_COLOR}>
            Using per-item share of expense and share of liability on each item.
          </Typography>
        )}
        <Stack direction="column" rowGap={1}>
          <SummaryRow label="Total Net Income:" value={income} />
          <SummaryRow label="Total Liabilities Repayments:" value={liabilities} />

          <SummaryRow label={getHemLabel(customerProfile)} value={hemBenchmark ?? 0} isUnused={isFullHemUnused} />
          {isHemSplit && (
            <SummaryRow
              label={`HEM applied (${hemSplitPercent}% split)`}
              value={hemApplied ?? 0}
              isUnused={isHemAppliedUnused}
            />
          )}

          <SummaryRow
            label="Stated Household Expenses"
            value={statedHouseholdExpenses}
            isUnused={isStatedExpensesUnused}
          />
          {rent > 0 && <SummaryRow label="Rent" value={rent} />}
          <SummaryRow label="Total Expenses:" value={expenses} />
        </Stack>
        <SummaryRow
          label="Surplus:"
          value={surplus}
          isBold
          valueColor={surplus >= 0 ? SURPLUS_TEXT_COLOR : DEFICIT_TEXT_COLOR}
        />
      </Stack>
    );
  },
);

export default ServiceabilityBreakdown;
